import React, { useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import { useI18n } from '@locales/client';

import {
  BodyTextBoldSecondary,
  BodyTextRegularSecondary,
} from '@/shared/ui/typography';
import { Box } from '@/shared/ui/box';
import { Condition } from '@/shared/ui/condition';
import { formatCurrency } from '@/shared/libs/format-currency';
import { useUser } from '@/shared/providers/user-provider/use-user';

import { useWalletHistory } from '@/features/wallet-managment/model/hooks';

import {
  HeaderIcon,
  Header,
  HistoryContent,
  HistoryStatus,
  HistoryItem,
  HeaderTitle,
  AnimatedSpinnerMedium,
  AnimatedSpinnerWrapper,
  HashLink,
} from './styles';

const HistoryDetail = ({ transaction, notifications }) => {
  const searchParams = useSearchParams();
  const openPayment = searchParams.get('openPayment') === 'true';
  const { walletHistory } = useWalletHistory(notifications?.transaction);
  const { user } = useUser();
  const t = useI18n();

  // Единый вычисляемый объект транзакции
  const currentTransaction = useMemo(() => {
    const fromHistory = walletHistory?.[0]?.transactions?.[0];
    let tx = openPayment ? fromHistory : transaction;

    // Обновление данных из уведомлений если совпадает ID
    const notificationTx = notifications?.transaction;
    if (
      notificationTx &&
      (notificationTx.id === transaction?.id ||
        notificationTx.id === fromHistory?.id)
    ) {
      tx = { ...tx, ...notificationTx };
    }

    return tx || transaction;
  }, [openPayment, walletHistory, transaction, notifications]);

  const status = currentTransaction?.status;
  const transactionType = currentTransaction?.type;

  return (
    <>
      <Header $status={status} $type={transactionType}>
        <Box>
          <HeaderTitle $status={status}>
            {formatCurrency(currentTransaction?.amountFiat, {
              currency: user.currency,
              currencySymbol: user.currencySymbol,
            })}
          </HeaderTitle>
        </Box>
        <Condition match={status === 'pending'}>
          <AnimatedSpinnerWrapper>
            <AnimatedSpinnerMedium />
          </AnimatedSpinnerWrapper>
        </Condition>
        <Condition match={status !== 'verification' && status !== 'pending'}>
          <HeaderIcon $type={transactionType} $status={status} />
        </Condition>
      </Header>

      <HistoryContent>
        <HistoryItem>
          <BodyTextRegularSecondary color="#AFAFAF">
            {t('wallet.history.status.title')}
          </BodyTextRegularSecondary>
          <HistoryStatus $status={status}>
            {t(`wallet.history.status.${status}`)}
          </HistoryStatus>
        </HistoryItem>

        <HistoryItem>
          <BodyTextRegularSecondary color="#AFAFAF">
            {t('wallet.history.id')}
          </BodyTextRegularSecondary>
          <BodyTextBoldSecondary>
            {currentTransaction?.id}
          </BodyTextBoldSecondary>
        </HistoryItem>

        <HistoryItem>
          <BodyTextRegularSecondary color="#AFAFAF">
            {t('wallet.history.currency')}
          </BodyTextRegularSecondary>
          <Box $display="flex" $gap={4} $alignItems="center">
            <BodyTextBoldSecondary>
              {currentTransaction?.transferCurrency}
            </BodyTextBoldSecondary>
          </Box>
        </HistoryItem>

        <HistoryItem>
          <BodyTextRegularSecondary color="#AFAFAF">
            {t('wallet.history.amount')}
          </BodyTextRegularSecondary>
          <Box $display="flex" $gap={4} $alignItems="center">
            <BodyTextBoldSecondary>
              {formatCurrency(currentTransaction?.amountFiat, {
                currency: user.currency,
                currencySymbol: user.currencySymbol,
              })}
            </BodyTextBoldSecondary>
          </Box>
        </HistoryItem>

        <Condition match={transactionType === 'withdraw'}>
          <HistoryItem>
            <BodyTextRegularSecondary color="#AFAFAF">
              {t('wallet.history.address')}
            </BodyTextRegularSecondary>
            <Box>
              <BodyTextBoldSecondary>
                {currentTransaction?.originalAddress || '-'}
              </BodyTextBoldSecondary>
            </Box>
          </HistoryItem>
        </Condition>

        <HistoryItem $last={true}>
          <BodyTextRegularSecondary color="#AFAFAF">
            {t('wallet.history.date')}
          </BodyTextRegularSecondary>
          <Box $wordWrap="break-word">
            <BodyTextBoldSecondary>
              {currentTransaction?.dateDetail}
            </BodyTextBoldSecondary>
          </Box>
        </HistoryItem>

        {currentTransaction?.hashLink && (
          <HashLink
            href={currentTransaction.hashLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('wallet.history.blockchain')}
          </HashLink>
        )}
      </HistoryContent>
    </>
  );
};

export { HistoryDetail };
